import type { PropsWithChildren, ReactNode } from "react";

import { Divider, Heading } from "@stacklet/ui";

interface Props extends PropsWithChildren {
  /** Anchor target for the client picker at the top of the page. */
  id: string;
  /** The client this section installs, e.g. "Claude Code". */
  title: string;
  /** One-click install control, rendered opposite the title. */
  action?: ReactNode;
}

/**
 * One client's install instructions, under a ruled heading.
 *
 * `tabIndex` and the heading's `aria-labelledby` are what make the picker's
 * jump usable: the section can take focus programmatically, and arriving there
 * announces which client's instructions these are. The heading is an h2 styled
 * down to the design's size, so the page outline runs h1 → h2 → h3.
 */
export function McpSection({ id, title, action, children }: Props) {
  const headingId = `${id}-heading`;

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-md outline-none"
      id={id}
      tabIndex={-1}
    >
      <div className="flex min-h-7 items-center justify-between gap-lg">
        <Heading className="text-title-medium" id={headingId} level={2}>
          {title}
        </Heading>
        {action}
      </div>

      <Divider />

      {children}
    </section>
  );
}
